import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mk, type MutationVars } from "~/state/mutations";
import {
  GUEST,
  generateId,
  useListsData,
  useOwnerId,
  writeGuest,
  type List,
  type Task,
} from "~/state/sync";
import { LIST_COLORS } from "~/utils/cadence";

export type ListInput = {
  name: string;
  color?: string | null;
  archived?: boolean;
};

function byName(a: List, b: List) {
  return a.name.localeCompare(b.name);
}

export function useLists() {
  const { data } = useListsData();
  return useMemo(() => (data ?? []).slice().sort(byName), [data]);
}

export function useList(listId: string | undefined) {
  const { data } = useListsData();
  return useMemo(
    () => (listId ? ((data ?? []).find((l) => l.id === listId) ?? null) : null),
    [data, listId],
  );
}

export function useListActions() {
  const userId = useOwnerId();
  const queryClient = useQueryClient();
  const scope = userId ?? GUEST;

  const createM = useMutation<unknown, Error, MutationVars["listCreate"]>({
    mutationKey: [...mk.listCreate],
  });
  const updateM = useMutation<unknown, Error, MutationVars["listUpdate"]>({
    mutationKey: [...mk.listUpdate],
  });
  const removeM = useMutation<unknown, Error, MutationVars["listRemove"]>({
    mutationKey: [...mk.listRemove],
  });

  function create(input: ListInput) {
    const id = generateId();
    const now = new Date().toISOString();
    const row: List = {
      id,
      owner_id: userId ?? GUEST,
      name: input.name.trim(),
      color: input.color ?? LIST_COLORS[0],
      archived: input.archived ?? false,
      created_at: now,
      updated_at: now,
      deleted: false,
      deleted_at: null,
      pinned: false,
    };
    if (userId) {
      createM.mutate({ scope, row });
    } else {
      writeGuest<List>(queryClient, "lists", (list) => [...list, row]);
    }
    return id;
  }

  function updateFields(input: Partial<ListInput>): Partial<List> {
    const fields: Partial<List> = {};
    if (input.name !== undefined) fields.name = input.name.trim();
    if (input.color !== undefined) fields.color = input.color;
    if (input.archived !== undefined) fields.archived = input.archived;
    return fields;
  }

  function update(listId: string, input: Partial<ListInput>) {
    const fields = updateFields(input);
    if (Object.keys(fields).length === 0) return;
    if (userId) {
      updateM.mutate({ scope, id: listId, fields });
    } else {
      writeGuest<List>(queryClient, "lists", (list) =>
        list.map((l) => (l.id === listId ? { ...l, ...fields } : l)),
      );
    }
  }

  /** Awaitable variant of `update`, for callers that must know it landed. */
  async function updateAsync(listId: string, input: Partial<ListInput>) {
    const fields = updateFields(input);
    if (Object.keys(fields).length === 0) return;
    if (userId) {
      await updateM.mutateAsync({ scope, id: listId, fields });
    } else {
      writeGuest<List>(queryClient, "lists", (list) =>
        list.map((l) => (l.id === listId ? { ...l, ...fields } : l)),
      );
    }
  }

  function remove(listId: string) {
    if (userId) {
      removeM.mutate({ scope, id: listId });
    } else {
      writeGuest<Task>(queryClient, "tasks", (list) =>
        list.filter((t) => t.list_id !== listId),
      );
      writeGuest<List>(queryClient, "lists", (list) =>
        list.filter((l) => l.id !== listId),
      );
    }
  }

  return { create, update, updateAsync, remove };
}
